import React, { useState, useRef, useEffect } from 'react';
import { useClient } from '../context/ClientContext';
import { Building2, ChevronDown, Check, Users, AlertCircle } from 'lucide-react';

const ClientSwitcher = ({ collapsed = false }) => {
  const { activeClient, clients, loading, switchClient } = useClient();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle client selection
  const handleSelect = (client) => {
    switchClient(client);
    setIsOpen(false);
    // Reload page to refresh all data for new client
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="px-3 py-2">
        <div className="animate-pulse bg-gray-200 h-10 rounded-lg"></div>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className={`px-3 py-2 ${collapsed ? 'hidden' : ''}`}>
        <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
          <AlertCircle className="w-4 h-4" />
          {!collapsed && <span>No active clients</span>}
        </div>
      </div>
    );
  }

  // Collapsed view - just show icon
  if (collapsed) {
    return (
      <div className="px-3 py-2">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-center p-2 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
          title={activeClient?.name || 'Select Client'}
        >
          <Building2 className="w-5 h-5 text-blue-600" />
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 py-2" ref={dropdownRef}>
      <div className="relative">
        {/* Dropdown trigger */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center gap-3 px-3 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg hover:from-blue-100 hover:to-indigo-100 transition-all shadow-sm"
          data-testid="client-switcher-trigger"
        >
          <div className="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-lg">
            <Building2 className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 text-left overflow-hidden">
            <p className="text-xs text-gray-500 font-medium">Active Client</p>
            <p className="text-sm font-semibold text-gray-800 truncate">
              {activeClient?.name || 'Select Client'}
            </p>
          </div>
          <ChevronDown 
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
            <div className="p-2">
              <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-gray-500 font-medium">
                <Users className="w-3 h-3" />
                <span>Switch Client ({clients.length})</span>
              </div>
              
              {clients.map((client) => (
                <button
                  key={client.id}
                  onClick={() => handleSelect(client)}
                  className={`w-full flex items-center gap-3 px-2 py-2 rounded-md transition-colors ${
                    activeClient?.id === client.id
                      ? 'bg-blue-50 text-blue-700'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                  data-testid={`client-option-${client.id}`}
                >
                  <div className={`w-6 h-6 rounded-md flex items-center justify-center ${
                    activeClient?.id === client.id ? 'bg-blue-600' : 'bg-gray-200'
                  }`}>
                    <Building2 className={`w-3 h-3 ${
                      activeClient?.id === client.id ? 'text-white' : 'text-gray-500'
                    }`} />
                  </div>
                  <span className="flex-1 text-left text-sm truncate">{client.name}</span>
                  {activeClient?.id === client.id && (
                    <Check className="w-4 h-4 text-blue-600" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientSwitcher;
